import { computed, ref } from 'vue';

import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { COMMUNITY } from '@/app/dashboards/reportConstants';
import { getAllAvailableCompetitorIdsByChannel } from '@/app/dashboards/utils/competitive.utils';
import { getDashboardChannelIconName } from '@/app/dashboards/utils/report.utils';
import {
  METRIC_TYPE_REPORT_KEYS,
  REPORT_CATEGORY_OPTIONS,
} from '@/app/dashboards/utils/reports.enum';
import { TEMPLATES } from '@/app/dashboards/utils/templates.enum';
import { BRAND } from '@/models/auth/permissions.enum';
import { CHANNELS, COMPETITIVE_CHANNELS } from '@/models/dashboards/channels.enum';
import { METRICS } from '@/models/dashboards/metrics';
import { useAuthStore } from '@/stores/auth';
import { useCommunityMessageTagsStore } from '@/stores/community-message-tags';
import { useContentTagsStore } from '@/stores/content-tags';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useSearchStore } from '@/stores/search';
import { computedObject } from '@/utils/computedObject';
import { getMetricDetails } from '@/utils/metrics';

export function useDashboards() {
  const dashboardReportsStore = useDashboardReportsStore();
  const searchStore = useSearchStore();
  const contentTagsStore = useContentTagsStore();
  const communityMessageTagsStore = useCommunityMessageTagsStore();
  const authStore = useAuthStore();
  const flagStore = useFlagStore();

  const identityStore = useIdentityStore();
  function canUserAccessDashboards(brand) {
    const brandLabel = brand.label;
    return identityStore.identity?.permissions?.dashboard?.[brandLabel]?.can_access_dashboards;
  }

  const canAccessDashboards = computed(() =>
    identityStore.allUserBrands.some(
      (brand) =>
        identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand) ||
        canUserAccessDashboards(brand) ||
        false,
    ),
  );

  const brandsWithCreatorAccess = computed(() =>
    Object.values(authStore.identity?.brands ?? {})
      .filter((brand) => brand.is_brand !== false)
      .filter((brand) => identityStore.guard(BRAND.CREATORS.CAN_ACCESS_CREATORS, brand)),
  );

  const brandsWithCreatorAccessIds = computed(() =>
    brandsWithCreatorAccess.value.map((brand) => brand.id),
  );
  const dashboardsStore = useDashboardsStore();
  const availableOwnedDashboards = computedObject(() =>
    dashboardsStore.ownedDashboards.map((dashboard) => ({
      value: dashboard.id,
      text: dashboard.name,
    })),
  );

  const availableSharedDashboards = computedObject(() =>
    dashboardsStore.sharedDashboards?.map((dashboard) => ({
      value: dashboard.id,
      text: dashboard.name,
    })),
  );

  const canAccessGDIDashboards = computed(
    () => flagStore?.ready && flagStore?.flags?.gdiDashboards,
  );

  const canAccessTsiBenchmarksFlag = computed(
    () => flagStore.ready && flagStore.flags.dashboardsTSIBenchmarks,
  );

  function canAccessTemplateFlags(template) {
    if (template?.requiredFlag) {
      return flagStore.flags[template.requiredFlag];
    }
    if (template?.hiddenRequiredFlag) {
      return !flagStore.flags[template.hiddenRequiredFlag];
    }
    return true;
  }

  const allTemplates = Object.values(TEMPLATES);
  const allReports = Object.values(REPORT_CATEGORY_OPTIONS);
  /*
    Selected user brand does not matter permissions-wise in Dashboards.
     At least one brand's organization mut have access.
 */
  function canAccessBrandOrUserPermissions(template) {
    const permissionResults = [];
    const {
      brandPermissions,
      userPermissions,
      organizationPermissions,
      organizationUserPermissions,
    } = template;
    if (
      !brandPermissions &&
      !userPermissions &&
      !organizationPermissions &&
      !organizationUserPermissions
    )
      return true;
    if (brandPermissions)
      permissionResults.push(identityStore.checkIfBrandPermissionsExist(brandPermissions));
    if (userPermissions) {
      permissionResults.push(identityStore.checkIfUserPermissionsExist(userPermissions));
    }
    if (organizationPermissions) {
      const brands = Object.values(identityStore.identity?.brands || {});
      organizationPermissions.forEach((permission) => {
        const hasAccess = brands.some((brand) => identityStore.guard(permission, brand));
        permissionResults.push(hasAccess);
      });
    }
    if (organizationUserPermissions) {
      const brands = Object.values(identityStore.identity?.brands || {});
      organizationUserPermissions.forEach((permission) => {
        const hasAccess = brands.some((brand) => identityStore.guard(permission, brand));
        permissionResults.push(hasAccess);
      });
    }
    return !permissionResults.some((permission) => permission === false);
  }

  function checkOptionFlags(option) {
    if (!option.requiredFlag && !option.requireSomeFlag) {
      return true;
    }
    const requireEveryFlag = Array.isArray(option.requiredFlag)
      ? option.requiredFlag
      : [option.requiredFlag];
    const requireSomeFlag = Array.isArray(option.requireSomeFlag)
      ? option.requireSomeFlag
      : [option.requireSomeFlag];
    const everyFlag =
      !option.requiredFlag || requireEveryFlag.every((flag) => this.flagStore.flags[flag]);
    const someFlag =
      !option.requireSomeFlag || requireSomeFlag.some((flag) => this.flagStore.flags[flag]);
    return everyFlag && someFlag;
  }

  function extractUniqueChannelIconNames(channels) {
    return [...new Set(channels?.map((channel) => getDashboardChannelIconName(channel)))];
  }

  function extractDashboardOwnerName(dashboard) {
    return authStore.identity.id === dashboard?.user_id ? null : dashboard?.owner_name;
  }

  const filteredTemplates = computedObject(() =>
    allTemplates
      .filter(
        (template) =>
          (canAccessBrandOrUserPermissions(template) || template?.showUpgrade) &&
          canAccessTemplateFlags(template),
      )
      .map((template) => ({
        ...template,
        locked: !canAccessBrandOrUserPermissions(template) && template?.showUpgrade,
      })),
  );

  const filteredReportOptions = computedObject(() =>
    allReports
      .filter((report) => checkOptionFlags(report))
      .map((report) => ({
        ...report,
        locked: !canAccessBrandOrUserPermissions(report),
      })),
  );

  const showContentTagsFilter = computed(
    () => canAccessGDIDashboards.value && !!contentTagsStore.allOrganizationsContentTags,
  );

  const showBrandFilter = computed(
    () =>
      canAccessGDIDashboards.value &&
      !identityStore.isShared &&
      dashboardsStore.dashboardIdentityBrands.length > 1,
  );

  const contentTagOptions = computedObject(() =>
    contentTagsStore.allOrganizationsContentTags
      .filter((contentTag) => contentTag.brandIds.length > 0)
      .map((contentTag) => ({
        value: contentTag.id,
        label: contentTag.name,
      })),
  );

  function getCompetitorOptions(channels) {
    const targetChannels = channels.filter((channel) => COMPETITIVE_CHANNELS.includes(channel));
    const allAvailableCompetitorIdsByChannel =
      getAllAvailableCompetitorIdsByChannel(targetChannels);

    const optionsPerChannel = targetChannels.flatMap((channel) => {
      const availableCompetitorIdsForChannel = allAvailableCompetitorIdsByChannel[channel];
      const sourceChannel = CHANNELS[channel].competitiveChannelType;
      const channelCompetitors = searchStore.competitors?.[sourceChannel] ?? [];
      return channelCompetitors
        .filter((competitor) =>
          availableCompetitorIdsForChannel.includes(competitor.sourceAccountId),
        )
        .map((competitor) => ({
          value: `${competitor.source}_${competitor.sourceAccountId}`,
          label: competitor.handle,
          avatar: competitor.avatarUrl,
          channel: competitor.source,
        }));
    });

    return optionsPerChannel.sort((a, b) => a.label.localeCompare(b.label));
  }

  const competitorTagOptions = computedObject(() =>
    dashboardReportsStore.competitorTags
      .flatMap((competitorTags) => competitorTags.tags)
      .map((tag) => ({ value: tag.id, label: tag.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  );

  const showCompetitorTagFilter = computed(() => competitorTagOptions.value.length > 0);

  const hasCommunityReports = computed(() => {
    if (!dashboardsStore.reports) return false;

    const reports = Object.values(dashboardsStore.reports);

    return reports.some((report) => {
      const metrics = report?.meta?.metrics || [].concat(report?.meta?.metric || []);

      const reportChannel = report?.meta?.channels?.[0];

      if (!reportChannel) {
        return false;
      }

      return metrics.some((metric) => {
        const { dataSource } = useMetricDetails({
          metric: ref(metric),
          channels: ref(report?.meta?.channels),
          metricReportTypeKey: ref(METRIC_TYPE_REPORT_KEYS.METRICS_REPORT),
        });

        return dataSource.value === COMMUNITY;
      });
    });
  });

  const hasMessageTagFiltersApplied = computed(
    () => dashboardReportsStore.messageTagIds?.length > 0,
  );

  const showMessageTagsFilter = computed(
    () => hasCommunityReports.value || hasMessageTagFiltersApplied.value,
  );

  const messageTagOptions = computedObject(() => {
    if (!showMessageTagsFilter.value) return [];

    const allBrandIds = dashboardsStore.currentDashboardBrandIds || [];
    const brandGroups = [];

    allBrandIds.forEach((brandId) => {
      const brand = dashboardsStore.dashboardIdentityBrands.find((b) => b.id === brandId);
      const tags = communityMessageTagsStore.getSelectedBrandTags(brandId);

      if (brand && tags.length > 0) {
        brandGroups.push({
          value: `brand_${brandId}`,
          label: brand.handle || brand.label,
          type: 'brand',
          avatarUrl: brand.avatarUrl,
          children: tags
            .map((tag) => ({
              value: tag.id,
              label: tag.name,
              color: tag.color,
              type: 'tag',
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        });
      }
    });
    return brandGroups.sort((a, b) => a.label.localeCompare(b.label));
  });

  const canAccessIndustryBenchmarks = computed(() =>
    identityStore.allUserBrands.some(
      (brand) =>
        identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS, brand) || false,
    ),
  );

  function checkIfUserOrBrandCanAccessDashboards(brand) {
    return (
      identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand) ||
      canUserAccessDashboards(brand)
    );
  }

  function canAccessMetrics(channel, metric, metricTypeReportKey, brands = null) {
    const { displayName, permissions } = getMetricDetails(metric, [channel], metricTypeReportKey);
    const metricPermissions = displayName ? permissions : METRICS[channel]?.[metric]?.permissions;
    return identityStore.checkIfBrandPermissionsExist(metricPermissions, brands);
  }

  function showNoAccessToMetric(
    reportMetricDetails,
    reportChannel,
    reportBrands,
    reportMetric,
    metricTypeReportKey,
  ) {
    //  Permissions may not exist even when new config is being used, so check a different field
    // TODO 104187: Remove if statement. Return what is currently inside of the if block
    if (reportMetricDetails?.displayName) {
      const someBrandCanAccessMetric = identityStore.checkIfBrandPermissionsExist(
        reportMetricDetails?.permissions,
        reportBrands,
      );
      const requiredFeatureFlag = reportMetricDetails?.requiredFeatureFlag;
      const hasRequiredFlag = Boolean(
        !requiredFeatureFlag || (flagStore.ready && flagStore.flags?.[requiredFeatureFlag]),
      );
      return !hasRequiredFlag || !someBrandCanAccessMetric;
    }
    return !canAccessMetrics(reportChannel, reportMetric, metricTypeReportKey, reportBrands);
  }

  return {
    availableOwnedDashboards,
    availableSharedDashboards,
    canAccessGDIDashboards,
    filteredTemplates,
    canAccessDashboards,
    canAccessIndustryBenchmarks,
    canAccessTsiBenchmarksFlag,
    filteredReportOptions,
    showContentTagsFilter,
    showBrandFilter,
    showCompetitorTagFilter,
    showMessageTagsFilter,
    hasCommunityReports,
    contentTagOptions,
    messageTagOptions,
    getCompetitorOptions,
    competitorTagOptions,
    canAccessMetrics,
    extractDashboardOwnerName,
    extractUniqueChannelIconNames,
    checkIfUserOrBrandCanAccessDashboards,
    showNoAccessToMetric,
    brandsWithCreatorAccess,
    brandsWithCreatorAccessIds,
  };
}
